import React, { useEffect, useState } from 'react';
import axiosInstance from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { parseISO, isValid, format } from 'date-fns';
import '../../styles/EditTemplate.css';
import Notification from '../notification/Notification';
import 'react-datepicker/dist/react-datepicker.css';
// import { Circles } from 'react-loader-spinner';
import ResumeSkeleton from '../Loading/ResumeSkeleton';

const EditTemplate = () => {
    const [template, setTemplate] = useState({
        aboutMe: '',
        languages: [],
        skills: [],
        softwareSkills: [],
        certificates: [],
        hobbies: [],
        workExperience: [],
        education: [],
        links: [],
        contact: {},
        image: ''
    });
    const [personName, setPersonName] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const [isImageSizeValid, setIsImageSizeValid] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                const response = await axiosInstance.get('/api/userTemplates/current');
                const templateData = response.data || {};

                const linksArray = Object.entries(templateData.links || {}).map(([platform, url]) => ({
                    platform,
                    url,
                }));

                setTemplate({
                    ...templateData,
                    links: linksArray,
                });
                setPersonName(templateData.personName);

                const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://venusresume.vercel.app/';
                const imageUrl = templateData?.image?.startsWith('/')
                    ? `${baseUrl}${templateData.image}`
                    : templateData.image;

                setImagePreview(imageUrl);
                setLoading(false);
            } catch (error) {
                //console.error('Error fetching template data:', error);
                setError('Error fetching template data');
                setLoading(false);
            }
        };

        fetchTemplate();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('contact.')) {
            const contactField = name.split('.')[1];
            setTemplate({ ...template, contact: { ...template.contact, [contactField]: value } });
        } else {
            setTemplate({ ...template, [name]: value });
        }
    };

    const handleArrayChange = (field, index, subField, value) => {
        const updatedArray = [...template[field]];
        if (subField) {
            updatedArray[index][subField] = value;
        } else {
            updatedArray[index] = value;
        }
        setTemplate({ ...template, [field]: updatedArray });
    };

    const handleAddItem = (field) => {
        let newItem;
        switch (field) {
            case 'certificates':
                newItem = { title: '', institution: '', DateReceived: '' };
                break;
            case 'workExperience':
                newItem = { role: '', company: '', location: '', startDate: '', endDate: '', description: '' };
                break;
            case 'education':
                newItem = { degree: '', institution: '', location: '', startDate: '', endDate: '', description: '' };
                break;
            case 'links':
                newItem = { platform: '', url: '' };
                break;
            default:
                newItem = '';
                break;
        }
        setTemplate({ ...template, [field]: [...template[field], newItem] });
    };

    const handleRemoveItem = (field, index) => {
        const updatedArray = template[field]?.filter((_, i) => i !== index);
        setTemplate({ ...template, [field]: updatedArray });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // Check if the file size exceeds 4MB
                setNotification({ message: 'Ensure that your photo size is 4MB or less.', isError: true });
                setIsImageSizeValid(false);
                setImagePreview(null);
                setTemplate({ ...template, image: '' });
                return;
            }
            setIsImageSizeValid(true); // Reset the validation flag if the file size is valid
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setTemplate({ ...template, image: file });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveChanges = async () => {

        if (!isImageSizeValid) {
            setNotification({ message: 'Ensure that your photo size is 4MB or less.', isError: true });
            return;
        }

        setSaving(true);

        try {
            const formData = new FormData();

            formData.append('personName', personName);

            const templateCopy = { ...template };
            templateCopy.links = template.links.reduce((acc, { platform, url }) => {
                acc[platform] = url;
                return acc;
            }, {});

            formData.append('templateData', JSON.stringify(templateCopy));

            if (template.image) {
                formData.append('image', template.image);
            }

            await axiosInstance.put('/api/userTemplates/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSaving(false);
            setNotification({ message: 'Changes saved successfully.', isError: false });
        } catch (error) {
            setNotification({ message: 'Ensure that your photo size is 4MB or less.', isError: true });
            //console.error('Error saving changes:', error);
        }
        finally {
            setSaving(false); // Set saving state to false
        }
    };

    const handleGetResumeClick = async () => {
        if (!template) {
            setNotification({ message: 'No recent template available. Please create a resume first.', isError: true });
            return;
        }

        try {
            setLoading(true);
            const response = await axiosInstance.get(`/api/userTemplates/current`);
            const userTemplate = response.data;

            setLoading(false);
            if (userTemplate) {
                setTemplate(userTemplate);
                setPersonName(userTemplate.personName);
                navigate(`/user-resume`);
            } else {
                setNotification({ message: 'Error fetching favorite template.', isError: true });
            }
        } catch (error) {
            // console.error('Error fetching favorite template:', error);
            setLoading(false);
            setNotification({ message: 'Error fetching favorite template.', isError: true });
        }
    };

    const getFormattedLabel = (fieldName) => {
        switch (fieldName) {
            case 'DateReceived':
                return 'Date Received';
            case 'startDate':
                return 'Start Date';
            case 'endDate':
                return 'End Date';
            default:
                return fieldName;
        }
    };

    const renderArrayField = (field, placeholder, subFields) => {
        const arrayField = template[field] || [];
        return (
            <div className='array-item-container'>
                <label className='label'>{placeholder}</label>
                {arrayField.map((item, index) => (
                    <div key={index} className="array-item">
                        <div className="input-group">
                            {subFields ? (
                                subFields.map(subField => (
                                    subField.name === 'DateReceived' || subField.name === 'startDate' || subField.name === 'endDate' ? (
                                        <div key={`${field}-${index}-${subField.name}`} className="date-picker">
                                            <label>{getFormattedLabel(subField.name)}</label>
                                            <DatePicker
                                                selected={item[subField.name] && isValid(parseISO(item[subField.name])) ? parseISO(item[subField.name]) : null}
                                                onChange={(date) => handleArrayChange(field, index, subField.name, date ? format(date, 'yyyy-MM-dd') : '')}
                                                dateFormat="yyyy-MM-dd"
                                                placeholderText={subField.placeholder}
                                                className='date'
                                            />
                                        </div>
                                    ) : (
                                        subField.name === 'description' ? (
                                            <textarea
                                                key={`${field}-${index}-${subField.name}`}
                                                placeholder={subField.placeholder}
                                                value={item[subField.name] || ''}
                                                onChange={(e) => handleArrayChange(field, index, subField.name, e.target.value)}
                                                className='textarea-field'
                                            />
                                        ) : (
                                            <input
                                                key={`${field}-${index}-${subField.name}`}
                                                type="text"
                                                placeholder={subField.placeholder}
                                                value={item[subField.name] || ''}
                                                onChange={(e) => handleArrayChange(field, index, subField.name, e.target.value)}
                                                className='input-field'
                                            />
                                        )
                                    )
                                ))
                            ) : (
                                <input
                                    key={`${field}-${index}`}
                                    type="text"
                                    placeholder={placeholder}
                                    value={item || ''}
                                    onChange={(e) => handleArrayChange(field, index, null, e.target.value)}
                                    className='input-field'
                                />
                            )}
                        </div>
                        <button onClick={() => handleRemoveItem(field, index)} className='remove-button'>X</button>
                    </div>
                ))}
                <button onClick={() => handleAddItem(field)} className='add-button'>Add {placeholder}</button>
            </div>
        );
    };

    const renderImageUpload = () => (
        <div className='image-upload-container'>
            <label htmlFor="imageUpload" className='label'>Upload Photo</label>
            <input type="file" id="imageUpload" onChange={handleImageChange} className='file-input' />
            {imagePreview && <img src={imagePreview} alt="Preview" className='image-preview' />}
        </div>
    );

    return (
        <div className='edit-template-form'>
            {loading ? (
                <ResumeSkeleton />
            ) : error ? (
                <p className='error-message'>{error}</p>
            ) : (
                <div className="form-content">
                    {renderImageUpload()}
                    <label className='field-group-profile'>Your photo size must be 4MB or less.</label>
                    <div className="field-group">
                        <label htmlFor="personName" className='label'>Person Name</label>
                        <input
                            type="text"
                            id="personName"
                            value={personName}
                            onChange={(e) => setPersonName(e.target.value)}
                            className='input-field'
                        />
                    </div>

                    <div className='field-group'>
                        <label htmlFor="aboutMe" className='label'>About Me</label>
                        <textarea
                            id="aboutMe"
                            name="aboutMe"
                            value={template.aboutMe}
                            onChange={handleInputChange}
                            className='textarea-field'
                        />
                    </div>

                    <div className="field-group">
                        <label className='label'>Contact Details</label>
                        <input
                            type="text"
                            placeholder="Phone"
                            name="contact.phone"
                            value={template.contact.phone || ''}
                            onChange={handleInputChange}
                            className='input-field'
                        />
                        <input
                            type="text"
                            placeholder="Email"
                            name="contact.email"
                            value={template.contact.email || ''}
                            onChange={handleInputChange}
                            className='input-field'
                        />
                        <input
                            type="text"
                            placeholder="Address"
                            name="contact.address"
                            value={template.contact.address || ''}
                            onChange={handleInputChange}
                            className='input-field'
                        />
                    </div>

                    {renderArrayField('languages', 'Languages')}
                    {renderArrayField('skills', 'Skills')}
                    {renderArrayField('softwareSkills', 'Software Skills')}
                    {renderArrayField('certificates', 'Certificates', [
                        { name: 'title', placeholder: 'Title' },
                        { name: 'institution', placeholder: 'Institution' },
                        { name: 'DateReceived', placeholder: 'Date Received' }
                    ])}
                    {renderArrayField('hobbies', 'Hobbies')}
                    {renderArrayField('workExperience', 'Work Experience', [
                        { name: 'role', placeholder: 'Role' },
                        { name: 'company', placeholder: 'Company' },
                        { name: 'location', placeholder: 'Location' },
                        { name: 'startDate', placeholder: 'Start Date' },
                        { name: 'endDate', placeholder: 'End Date' },
                        { name: 'description', placeholder: 'Description' }
                    ])}
                    {renderArrayField('education', 'Education', [
                        { name: 'degree', placeholder: 'Degree' },
                        { name: 'institution', placeholder: 'Institution' },
                        { name: 'location', placeholder: 'Location' },
                        { name: 'startDate', placeholder: 'Start Date' },
                        { name: 'endDate', placeholder: 'End Date' },
                        { name: 'description', placeholder: 'Description' }
                    ])}
                    {renderArrayField('links', 'Links', [
                        { name: 'platform', placeholder: 'Platform' },
                        { name: 'url', placeholder: 'URL' }
                    ])}

                    <button onClick={handleSaveChanges} className='save-button' disabled={!isImageSizeValid || saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button onClick={handleGetResumeClick} className='resume-button'>
                        Get Resume
                    </button>
                    {notification && (
                        <Notification
                            message={notification.message}
                            isError={notification.isError}
                            onClose={() => setNotification(null)}
                            className="notification"
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default EditTemplate;
